import { Loader2, Pencil, RefreshCw, Sparkles } from 'lucide-react';
import { useState } from 'react';
import {
  type OrgOnboardingPlan,
  type OrgOnboardingProfile,
  useApplyOrgOnboardingPlan,
  useGenerateOrgOnboardingPlan,
  useOrgOnboardingProfile,
  useUpdateOrgOnboardingProfile,
} from '../hooks/useAdminData';

interface ProfileDraft {
  industry: string;
  productSummary: string;
  salesMotion: string;
  targetAudience: string;
  tone: string;
  website: string;
  languages: string;
}

const EMPTY_DRAFT: ProfileDraft = {
  industry: '',
  productSummary: '',
  salesMotion: '',
  targetAudience: '',
  tone: '',
  website: '',
  languages: '',
};

function toDraft(profile: OrgOnboardingProfile): ProfileDraft {
  return {
    industry: profile.industry ?? '',
    productSummary: profile.productSummary ?? '',
    salesMotion: profile.salesMotion ?? '',
    targetAudience: profile.targetAudience ?? '',
    tone: profile.tone ?? '',
    website: profile.website ?? '',
    languages: profile.languages.join(', '),
  };
}

function valueOrFallback(value: string | null | undefined): string {
  return value?.trim() || 'Not set';
}

const inputClass =
  'w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors';

export function OrganizationOnboardingTab({ orgId }: Readonly<{ orgId: string }>) {
  const { data: profile, isLoading, isError } = useOrgOnboardingProfile(orgId);
  const updateProfile = useUpdateOrgOnboardingProfile(orgId);
  const generatePlan = useGenerateOrgOnboardingPlan(orgId);
  const applyPlan = useApplyOrgOnboardingPlan(orgId);

  const [draft, setDraft] = useState<ProfileDraft>(EMPTY_DRAFT);
  const [editing, setEditing] = useState(false);
  const [showRegenerateOffer, setShowRegenerateOffer] = useState(false);
  const [plan, setPlan] = useState<OrgOnboardingPlan | null>(null);
  const [planApplied, setPlanApplied] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-text-secondary">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading onboarding context...
      </div>
    );
  }

  if (isError || !profile) {
    return (
      <div className="rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger">
        Failed to load onboarding context.
      </div>
    );
  }

  const setField = (field: keyof ProfileDraft, value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const saveProfile = () => {
    updateProfile.mutate(
      {
        industry: draft.industry.trim(),
        productSummary: draft.productSummary.trim(),
        salesMotion: draft.salesMotion.trim(),
        targetAudience: draft.targetAudience.trim(),
        tone: draft.tone.trim(),
        website: draft.website.trim(),
        languages: draft.languages
          .split(',')
          .map((language) => language.trim())
          .filter(Boolean),
      },
      {
        onSuccess: () => {
          setEditing(false);
          setPlanApplied(false);
          setShowRegenerateOffer(true);
        },
      },
    );
  };

  const regenerate = () => {
    setShowRegenerateOffer(false);
    generatePlan.mutate(undefined, {
      onSuccess: (generatedPlan) => setPlan(generatedPlan),
    });
  };

  const toggleAgent = (type: string) => {
    setPlan((current) =>
      current
        ? {
            ...current,
            agents: current.agents.map((agent) =>
              agent.type === type ? { ...agent, enabled: !agent.enabled } : agent,
            ),
          }
        : current,
    );
  };

  const toggleSkill = (name: string) => {
    setPlan((current) =>
      current
        ? {
            ...current,
            skills: current.skills.map((skill) =>
              skill.name === name ? { ...skill, enabled: !skill.enabled } : skill,
            ),
          }
        : current,
    );
  };

  const applyGeneratedPlan = () => {
    if (!plan) return;
    applyPlan.mutate(plan, {
      onSuccess: () => {
        setPlan(null);
        setPlanApplied(true);
      },
    });
  };

  if (plan) {
    return (
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-text">Review generated AI plan</h3>
          </div>
          <p className="mt-1 text-sm text-text-secondary">
            Review every recommendation before applying it. Confidence:{' '}
            <span className="font-medium text-text">{Math.round(plan.confidence * 100)}%</span>
          </p>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          <PlanGroup title="Agents">
            {plan.agents.map((agent) => (
              <label
                key={agent.type}
                className="flex items-start gap-3 rounded-lg border border-border p-3"
              >
                <input
                  type="checkbox"
                  checked={agent.enabled}
                  onChange={() => toggleAgent(agent.type)}
                  className="mt-1 accent-primary"
                />
                <span>
                  <span className="block text-sm font-medium text-text">{agent.type}</span>
                  <span className="block text-xs text-text-secondary">{agent.rationale}</span>
                </span>
              </label>
            ))}
          </PlanGroup>

          <PlanGroup title="Skills">
            {plan.skills.length === 0 ? (
              <p className="text-sm text-text-muted">No skills recommended.</p>
            ) : (
              plan.skills.map((skill) => (
                <label
                  key={skill.name}
                  className="flex items-start gap-3 rounded-lg border border-border p-3"
                >
                  <input
                    type="checkbox"
                    checked={skill.enabled}
                    onChange={() => toggleSkill(skill.name)}
                    className="mt-1 accent-primary"
                  />
                  <span>
                    <span className="block text-sm font-medium text-text">{skill.name}</span>
                    <span className="block text-xs text-text-secondary">{skill.rationale}</span>
                  </span>
                </label>
              ))
            )}
          </PlanGroup>
        </div>

        {applyPlan.isError && (
          <p className="text-sm text-danger">Failed to apply the plan. Please try again.</p>
        )}

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={applyGeneratedPlan}
            disabled={applyPlan.isPending}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {applyPlan.isPending ? 'Applying...' : 'Apply plan'}
          </button>
          <button
            type="button"
            onClick={() => setPlan(null)}
            disabled={applyPlan.isPending}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-text hover:bg-surface-hover disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-text">Onboarding context</h3>
          <p className="mt-1 max-w-3xl text-sm text-text-secondary">
            Shared business context used to generate and guide this organization&apos;s AI agents.
          </p>
        </div>
        {!editing && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setDraft(toDraft(profile));
                setEditing(true);
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium text-text hover:bg-surface-hover"
            >
              <Pencil className="h-4 w-4" />
              Edit context
            </button>
            <button
              type="button"
              onClick={regenerate}
              disabled={generatePlan.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${generatePlan.isPending ? 'animate-spin' : ''}`} />
              {generatePlan.isPending ? 'Generating...' : 'Regenerate AI plan'}
            </button>
          </div>
        )}
      </div>

      {planApplied && (
        <div className="rounded-lg border border-success/30 bg-success/10 p-4 text-sm text-success">
          AI plan applied with the updated context.
        </div>
      )}

      {showRegenerateOffer && (
        <div className="rounded-lg border border-primary/30 bg-primary/10 p-4">
          <p className="font-medium text-text">Context saved</p>
          <p className="mt-1 text-sm text-text-secondary">
            The current AI setup was generated from the previous context. Regenerate it now?
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={regenerate}
              className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white"
            >
              Regenerate AI plan
            </button>
            <button
              type="button"
              onClick={() => setShowRegenerateOffer(false)}
              className="rounded-lg border border-border px-3 py-2 text-sm font-medium text-text"
            >
              Not now
            </button>
          </div>
        </div>
      )}

      {generatePlan.isError && (
        <div className="rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger">
          Failed to generate the AI plan. The saved context was not changed.
        </div>
      )}

      {editing ? (
        <div className="grid gap-5 lg:grid-cols-2">
          <Field label="Industry">
            <input
              value={draft.industry}
              onChange={(event) => setField('industry', event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Target audience">
            <input
              value={draft.targetAudience}
              onChange={(event) => setField('targetAudience', event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Product or service" fullWidth>
            <textarea
              value={draft.productSummary}
              onChange={(event) => setField('productSummary', event.target.value)}
              rows={4}
              className={inputClass}
            />
          </Field>
          <Field label="Sales motion">
            <input
              value={draft.salesMotion}
              onChange={(event) => setField('salesMotion', event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Brand tone">
            <input
              value={draft.tone}
              onChange={(event) => setField('tone', event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Website">
            <input
              type="url"
              value={draft.website}
              onChange={(event) => setField('website', event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Languages" hint="Comma-separated, for example: English, Spanish">
            <input
              value={draft.languages}
              onChange={(event) => setField('languages', event.target.value)}
              className={inputClass}
            />
          </Field>
          <div className="flex items-center gap-3 lg:col-span-2">
            <button
              type="button"
              onClick={saveProfile}
              disabled={
                updateProfile.isPending ||
                !draft.industry.trim() ||
                !draft.productSummary.trim() ||
                !draft.targetAudience.trim()
              }
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
            >
              {updateProfile.isPending ? 'Saving...' : 'Save context'}
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(toDraft(profile));
                setEditing(false);
              }}
              disabled={updateProfile.isPending}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-text hover:bg-surface-hover disabled:opacity-50"
            >
              Cancel
            </button>
            {updateProfile.isError && (
              <span className="text-sm text-danger">Failed to save context.</span>
            )}
          </div>
        </div>
      ) : (
        <dl className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <ProfileValue label="Industry" value={valueOrFallback(profile.industry)} />
          <ProfileValue label="Target audience" value={valueOrFallback(profile.targetAudience)} />
          <ProfileValue label="Sales motion" value={valueOrFallback(profile.salesMotion)} />
          <ProfileValue label="Brand tone" value={valueOrFallback(profile.tone)} />
          <ProfileValue label="Website" value={valueOrFallback(profile.website)} />
          <ProfileValue
            label="Languages"
            value={profile.languages.length > 0 ? profile.languages.join(', ') : 'Not set'}
          />
          <ProfileValue
            label="Product or service"
            value={valueOrFallback(profile.productSummary)}
            fullWidth
          />
        </dl>
      )}
    </div>
  );
}

function Field({
  label,
  hint,
  fullWidth,
  children,
}: Readonly<{
  label: string;
  hint?: string;
  fullWidth?: boolean;
  children: React.ReactNode;
}>) {
  return (
    <label className={fullWidth ? 'lg:col-span-2' : undefined}>
      <span className="mb-1 block text-sm font-medium text-text-secondary">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-text-muted">{hint}</span>}
    </label>
  );
}

function ProfileValue({
  label,
  value,
  fullWidth,
}: Readonly<{ label: string; value: string; fullWidth?: boolean }>) {
  return (
    <div className={`rounded-lg border border-border bg-background p-4 ${fullWidth ? 'sm:col-span-2 lg:col-span-3' : ''}`}>
      <dt className="text-xs font-medium uppercase tracking-wide text-text-muted">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap text-sm text-text">{value}</dd>
    </div>
  );
}

function PlanGroup({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="rounded-xl border border-border bg-background p-4">
      <h4 className="mb-3 font-medium text-text">{title}</h4>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
